import React from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import Hr from 'components/Common/Hr'
import HelpPane from 'components/Common/HelpPane'
import FlexibleCancellationCharterFlightFees from './FlexibleCancellationCharterFlightFees'

const ModalContent = styled.div`
  padding-top: ${rem(16)};
`

const CancellationTermsWrapper = styled.div`
  display: grid;
  grid-column-gap: ${rem(18)};
  margin-top: ${rem(40)};

  ${mediaQueryUp.tablet} {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
  }
`

const TermsGrid = styled.div`
  display: grid;
  grid-column-gap: ${rem(16)};
  grid-row-gap: ${rem(18)};
  grid-template-columns: 1fr 2fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

interface Props {
  checkInDate?: string | moment.Moment
  startBasedOnToday: moment.Moment
  startBasedOnCheckIn: moment.Moment | undefined
  cancellationAfterChangeOfMindPeriod: boolean
  days: number
}

function OfferPropertyFlexibleCancellationCharterPolicyModal(props: Props) {
  const {
    checkInDate,
    startBasedOnToday,
    cancellationAfterChangeOfMindPeriod,
    startBasedOnCheckIn,
    days,
  } = props

  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  const conditionText = !checkInDate ? intl.formatMessage({
    id: '0DHGEp6sko',
    defaultMessage: ' (provided cancellation is made at least 14 days prior to departure)',
    description: 'Condition text for charter cancellation refund',
  }) : ''

  return <Modal
    title={intl.formatMessage({
      id: 'UUX1Q66CrL',
      defaultMessage: 'Cancellation policy',
      description: 'Title for flexible cancellation policy modal',
    })}
    size="L"
  >
    <ModalContent>
      <BodyText variant="medium" as="p">
        {intl.formatMessage({
          id: 'm7DkNdGSUK',
          defaultMessage: 'Things don’t always work out, so enjoy peace of mind knowing that the following cancellation options are available to you.',
          description: 'Introductory text for flexible cancellation policy modal',
        })}
      </BodyText>
      <CancellationTermsWrapper>
        <TermsGrid>
          {!checkInDate && <BodyText variant="medium" weight="bold" as="span">
            {intl.formatMessage({
              id: 'rQ/eM2Pk1s',
              defaultMessage: 'Up to 7 days after purchase',
              description: 'Cancellation period label for up to 7 days after purchase',
            })}
          </BodyText>}
          {checkInDate && <BodyText variant="medium" weight="bold" as="span">
            {intl.formatMessage({
              id: 'N2tJOm1tmT',
              defaultMessage: 'Cancel until {date}',
              description: 'Cancellation deadline label with date',
            }, {
              date: intlDateFormatter(startBasedOnToday, INTL_DMY_CASUAL_SHORT_FORMAT),
            })}
          </BodyText>}
          <BodyText variant="medium" as="span">
            {intl.formatMessage({
              id: 'j8Vy93vOtF',
              defaultMessage: '100% refund of total purchase price to your original payment method or credit{condition}',
              description: 'Refund description for charter flexible cancellation policy',
            }, {
              condition: conditionText,
            })}
          </BodyText>
        </TermsGrid>
        <Hr size="md" />
        {cancellationAfterChangeOfMindPeriod && startBasedOnCheckIn && <>
          <TermsGrid>
            <BodyText variant="medium" weight="bold" as="span">
              {intl.formatMessage({
                id: 'vV6/tOpe4m',
                defaultMessage: 'Cancel until {date}',
                description: 'Cancellation deadline label with check-in based date',
              }, {
                date: intlDateFormatter(startBasedOnCheckIn, INTL_DMY_CASUAL_SHORT_FORMAT),
              })}
            </BodyText>
            <FlexibleCancellationCharterFlightFees />
          </TermsGrid>
          <Hr size="md" />
        </>}
        {!cancellationAfterChangeOfMindPeriod && !checkInDate && <>
          <TermsGrid>
            <BodyText variant="medium" weight="bold" as="span">
              {intl.formatMessage({
                id: 'TPN99sOPyS',
                defaultMessage: 'Up to {days} days prior to departure',
                description: 'Charter cancellation period label with days count',
              }, {
                days: days + 1,
              })}
            </BodyText>
            <FlexibleCancellationCharterFlightFees />
          </TermsGrid>
          <Hr size="md" />
        </>}
        <TermsGrid>
          {!checkInDate && <BodyText variant="medium" weight="bold" as="span">
            {intl.formatMessage({
              id: 'kwsH34KRG8',
              defaultMessage: 'From {days} days until departure',
              description: 'Charter non-refundable period label with days count',
            }, {
              days,
            })}
          </BodyText>}
          {checkInDate && startBasedOnCheckIn && <BodyText variant="medium" weight="bold" as="span">
            {intl.formatMessage({
              id: 'ukq32bgEjE',
              defaultMessage: 'Cancel after {date}',
              description: 'Non-refundable period label with date',
            }, {
              date: intlDateFormatter(startBasedOnCheckIn, INTL_DMY_CASUAL_SHORT_FORMAT),
            })}
          </BodyText>}
          <BodyText variant="medium" as="span">
            {intl.formatMessage({
              id: 'PfoSB+eSSw',
              defaultMessage: 'Non-refundable',
              description: 'Label for non-refundable cancellation period',
            })}
          </BodyText>
        </TermsGrid>
      </CancellationTermsWrapper>
      <HelpPane />
    </ModalContent>
  </Modal>
}

export default OfferPropertyFlexibleCancellationCharterPolicyModal
